import { githubAuthApiRef, useApi, useApiHolder } from '@backstage/core-plugin-api';
import { TextField } from '@material-ui/core';
import { useEffect } from 'react';
import { useAsync } from 'react-use';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  catalogApiRef,
  entityPresentationApiRef,
  entityPresentationSnapshot,
  useEntityPresentation,
} from '@backstage/plugin-catalog-react';
import { Entity, parseEntityRef, stringifyEntityRef } from '@backstage/catalog-model';
import { FieldValidation } from '@rjsf/utils';
import { unifiedGithubApiRef } from '../../services';
import { GithubOrgPrefix } from '../GithubOrgPrefix';
import type { GithubDestination } from '../../services/unifiedGithubService/unifiedGithubService.types';
import { entityMatchesGithubTeam, findGithubTeamForEntity, isInvalidGithubTeam } from './githubTeamMatching';

const GithubTeamOptionLabel = ({ entity }: { entity: Entity | string }) => {
  const { primaryTitle } = useEntityPresentation(entity, { defaultKind: 'Group' });
  return <>{primaryTitle}</>;
};

export type GithubTeamSelectComponentProps = {
  onChange: (event: string) => void;
  formData: string;
  /**
   * Fixed organization the selected team must belong to. When provided, teams
   * are queried for that organization only, the prefix never changes, and a
   * selection that is not an eligible team in that organization is cleared.
   * When omitted, the legacy feature-flag routing is used.
   */
  destination?: GithubDestination;
};

export const GithubTeamSelect = (props: GithubTeamSelectComponentProps) => {
  const { onChange, formData, destination } = props;
  const unifiedGithubApi = useApi(unifiedGithubApiRef);
  const catalogApi = useApi(catalogApiRef);
  const ghecAuthApi = useApi(githubAuthApiRef);
  const entityPresentationApi = useApiHolder().get(entityPresentationApiRef);
  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    const backstageUserIdentity = await ghecAuthApi.getBackstageIdentity();

    if (!backstageUserIdentity?.identity?.userEntityRef) {
      throw new Error('No GitHub username found for this account.');
    }

    const { name: githubUsername } = parseEntityRef(backstageUserIdentity.identity.userEntityRef);
    const [{ items }, githubTeams] = await Promise.all([
      catalogApi.getEntities({ filter: { kind: ['Group'] } }),
      unifiedGithubApi.getUsersGithubTeams({ githubUsername, origin: destination?.origin }),
    ]);

    const eligibleTeams = githubTeams.filter(
      team =>
        !isInvalidGithubTeam(team) &&
        (!destination || team.org?.toLowerCase() === destination.organization.toLowerCase()),
    );
    const eligibleGroups = items.filter(entity => eligibleTeams.some(team => entityMatchesGithubTeam(entity, team)));

    if (eligibleGroups.length === 0) {
      throw new Error(
        destination
          ? `No eligible GitHub teams found in ${destination.organization}. Please request access to an eligible GitHub team in ${destination.organization} and try again.`
          : 'No eligible GitHub teams found. Please request access to an eligible GitHub team and try again.',
      );
    }

    return { entities: eligibleGroups, teams: eligibleTeams };
  }, [destination?.organization, destination?.origin]);

  const selectedEntity = entities?.entities.find(entity => stringifyEntityRef(entity) === formData);
  const organization =
    destination?.organization ?? findGithubTeamForEntity(selectedEntity, entities?.teams ?? [])?.org ?? 'AAInternal';

  // With a fixed destination, never keep a selection that cannot be verified as
  // an eligible team in that organization (e.g. carried over from another org).
  useEffect(() => {
    if (!destination || loading || !formData) {
      return;
    }
    if (!selectedEntity) {
      onChange('');
    }
  }, [destination, selectedEntity, formData, loading, onChange]);

  return (
    <Autocomplete
      id="github-team"
      value={!entities ? formData : selectedEntity}
      loading={loading}
      onChange={(_, selection) => {
        // Strings are saved values displayed while loading, not selectable options.
        if (typeof selection !== 'string') {
          onChange(selection ? stringifyEntityRef(selection) : '');
        }
      }}
      options={entities?.entities || []}
      autoSelect
      freeSolo={false}
      style={{ width: '100%' }}
      getOptionLabel={option =>
        typeof option === 'string'
          ? option
          : entityPresentationSnapshot(option, { defaultKind: 'Group' }, entityPresentationApi).primaryTitle
      }
      renderOption={option => <GithubTeamOptionLabel entity={option} />}
      renderInput={params => (
        <TextField
          {...params}
          label="GitHub Team"
          error={error !== undefined}
          helperText={error && error.message}
          FormHelperTextProps={{ margin: 'dense', style: { marginLeft: 0 } }}
          variant="outlined"
          required
          InputProps={{ ...params.InputProps, startAdornment: <GithubOrgPrefix organization={organization} /> }}
        />
      )}
    />
  );
};

export const githubTeamValidation = (value: string, validation: FieldValidation) => {
  if (!value) {
    validation.addError('Please select a github team');
  }
};
